/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2024, Zumper
 * @description SpotlightListingInfo
 */

import { selectFilters } from '@zcode-ui/filters'
import { useRouteProps } from '@zcode-ui/hook'
import { Close } from '@zcode-ui/icons'
import { Dots } from '@zcode-ui/loaders'
import {
  ListingCardAmenities,
  ListingCardDescription,
  ListingCardReview,
  ListingCardReviewsSummary,
  ListingCardSupplementalDescription,
} from '@zcode-ui/web-zumper-features'
import buttonCss from '@zcode-ui/web-zumper-features/src/ListingCard/buttons.module.scss'
import { VerifiedIcon } from '@zcode-ui/web-zumper-ui'
import { toTitleCase } from '@zcode/nr/es/shared/utils'
import type { CardBackResponse, Listable } from '@zcode/state-type'
import cn from 'classnames'
import { format } from 'date-fns'
import React, { memo, useMemo, Fragment, type MouseEvent } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { selectFiltersPath, selectIsHFR } from '../../../../../modules/router/selectors'
import { ListCardRating } from '../../../../detail/RatingsAndReviews/ListCardRating/ListCardRating'
import { createGetHoodPropertyText } from '../ListingCard/helpers/createGetHoodPropertyText'
import { updateAmenities } from '../ListingCard/helpers/updateAmenities'
import { useGetCommonEntityData } from '../ListingCard/helpers/useGetCommonEntityData'
import css from './SpotlightListingInfo.module.scss'

export interface SpotlightListingInfoProps {
  cardBackData?: CardBackResponse
  cardBackDataHasLoaded?: boolean
  entity: Listable
  handleQuickLookClick: (e: MouseEvent<HTMLButtonElement>) => void
  isCity: boolean
  isHood: boolean
  isVertical: boolean
  shouldShowDefaultInfo: boolean
}

export const SpotlightListingInfo = memo(
  ({
    cardBackData,
    cardBackDataHasLoaded,
    entity,
    handleQuickLookClick,
    isCity,
    isHood,
    isVertical,
    shouldShowDefaultInfo,
  }: SpotlightListingInfoProps) => {
    const getCommonEntityData = useGetCommonEntityData()
    const {
      dateAvailableDate,
      isVerified,
      rating,
      review: entityReview,
      reviewsSummary: entityReviewsSummary,
      shortDescription,
      supplementalDescription: entitySupplementalDescription,
    } = entity

    const {
      review: localReview,
      reviews_summary: localReviewsSummary,
      supple_description: localSupplementalDescription,
    } = cardBackData?.[entity.groupId] ?? {}
    const review = entityReview ?? localReview
    const reviewsSummary = entityReviewsSummary ?? localReviewsSummary
    const supplementalDescription = entitySupplementalDescription ?? localSupplementalDescription

    const { bedsRange, bathsRange } = getCommonEntityData({ entity })
    const routeProps = useRouteProps()
    const filtersPath = selectFiltersPath(routeProps)
    const filters = useSelector(selectFilters)
    const isHFR = selectIsHFR(routeProps)
    const updatedAmenities = useMemo(
      () =>
        updateAmenities({
          filters,
          filtersPath,
          entity,
          isHFR,
          limit: 3,
        }),
      [filters, filtersPath, entity, isHFR]
    )
    const amenities = updatedAmenities ?? entity.combinedAmenities
    const slicedAmenities = amenities.slice(0, 3)
    const titleCasedSlicedAmenities = slicedAmenities.map((amenity) => toTitleCase(amenity))

    const amenitiesText = entity.combinedAmenityTags
      .filter((amenity) => {
        const amenityToCompare = toTitleCase(amenity.replace(/-/g, ' '))
        return !titleCasedSlicedAmenities.includes(amenityToCompare)
      })
      .slice(0, 6)
    const location = useLocation()
    const hoodPropertyText = createGetHoodPropertyText(location)(entity)
    let availableDateText = ''
    const shouldFetchCardBack = shouldShowDefaultInfo || (isCity && !isVertical)

    if (dateAvailableDate) {
      availableDateText = `Available ${format(dateAvailableDate, 'MMM d')}`
    }

    if (!cardBackDataHasLoaded && shouldFetchCardBack) {
      return (
        <div className={css.loading}>
          <Dots />
        </div>
      )
    }

    return (
      <div className={css.container}>
        <button
          className={cn(buttonCss.baseButton, css.closeButton)}
          onClick={handleQuickLookClick}
          type="button"
        >
          <Close fill={'var(--chakra-colors-neutral-650)'} size={11.5} strokeWidth={2} />
        </button>
        <div className="overflow-y-auto h-[330px] flex flex-col no-scrollbar gap-y-6 pb-4">
          <div className={css.hoodContainer}>
            <p className={css.hoodText}>{hoodPropertyText}</p>
            {!!availableDateText && <p className={css.dateText}>{availableDateText}</p>}
          </div>
          <div className={css.infoContainer}>
            {(isVerified || rating) && (
              <div className={css.infoContent}>
                {rating && (
                  <ListCardRating
                    gapStyle={css.ratingGapStyle}
                    rating={rating}
                    ratingStyle={css.ratingStyle}
                    textStyle={css.ratingTextStyle}
                  />
                )}
                {rating && isVerified && <span className={css.divider}>|</span>}
                {isVerified && (
                  <div className={css.verifiedContainer}>
                    <VerifiedIcon
                      popoverId={`verified-${entity.uId}`}
                      isDirectDeal
                      isNewDetailPage
                      isWhite
                      size={18}
                    />
                    <p className={css.verifiedText}>Verified</p>
                  </div>
                )}
              </div>
            )}
            <div className={css.infoContent}>
              <p className={css.infoText}>{bedsRange}</p>
              <span className={css.divider}>|</span>
              <p className={css.infoText}>{bathsRange}</p>
            </div>
            <div className={css.inlineAmenityContainer}>
              <p className={css.inlineAmenityText}>
                {slicedAmenities.map((amenity, i) => {
                  const isLast = i === slicedAmenities.length - 1
                  return (
                    <Fragment key={amenity}>
                      {amenity}
                      {!isLast ? <span className={css.amenityDivider}>|</span> : null}
                    </Fragment>
                  )
                })}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-y-4">
            {shouldShowDefaultInfo ? (
              <>
                {supplementalDescription ? (
                  <ListingCardSupplementalDescription
                    isSpotlight
                    supplementalDescription={supplementalDescription}
                  />
                ) : null}
                {!supplementalDescription && shortDescription ? (
                  <ListingCardDescription description={shortDescription} isSpotlight />
                ) : null}
                {!supplementalDescription && !shortDescription && !!amenitiesText.length ? (
                  <ListingCardAmenities amenitiesList={amenitiesText} isSpotlight />
                ) : null}
                {reviewsSummary ? (
                  <ListingCardReviewsSummary isSpotlight reviewsSummary={reviewsSummary} />
                ) : null}
                {!reviewsSummary && review ? (
                  <ListingCardReview review={review} isSpotlight />
                ) : null}
              </>
            ) : (
              <>
                {isCity && !isVertical && supplementalDescription ? (
                  <ListingCardSupplementalDescription
                    isSpotlight
                    supplementalDescription={supplementalDescription}
                  />
                ) : null}
                {isHood && !isVertical && shortDescription ? (
                  <ListingCardDescription description={shortDescription} isSpotlight />
                ) : null}
                {isVertical && !!amenitiesText.length ? (
                  <ListingCardAmenities amenitiesList={amenitiesText} isSpotlight />
                ) : null}
                {isCity && !isVertical && reviewsSummary ? (
                  <ListingCardReviewsSummary isSpotlight reviewsSummary={reviewsSummary} />
                ) : null}
                {isCity && !isVertical && !reviewsSummary && review ? (
                  <ListingCardReview review={review} isSpotlight />
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    )
  }
)
